// General purpose helper functions

export const topic_url = (cref: string): string => `/guide/${encodeURIComponent(cref)}`;

export const sku_url = (cref: string): string => `/products/${encodeURIComponent(cref)}`;

export const strip_en = (s: string): string => s.replace('@en:', '');

export const isApplePlatform = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const platformInfo = `${navigator.platform} ${navigator.userAgent}`;
  return /Mac|iPhone|iPad|iPod/i.test(platformInfo);
};

export const getSubmitShortcutLabel = (): string => {
  if (typeof navigator === 'undefined') {
    return 'Ctrl+Enter or Cmd+Enter';
  }

  return isApplePlatform() ? 'Cmd+Enter' : 'Ctrl+Enter';
};

/** Returns a trimmed URL only when it uses a safe http/https scheme. */
export const getSafeHttpUrl = (url?: string | null): string | null => {
  const normalizedUrl = url?.trim();

  if (!normalizedUrl) {
    return null;
  }

  try {
    const parsedUrl = new URL(normalizedUrl);

    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
      ? parsedUrl.toString()
      : null;
  } catch {
    return null;
  }
};

// This must stay in sync with AI_CREATOR in the backend
export const isLLM = (userId: string): boolean => {
  return userId === 'AI';
};

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;
const MONTH_MS = 30 * DAY_MS;
const YEAR_MS = 365 * DAY_MS;

export const formatCompactRelativeTimestamp = (
  timestamp: string,
  now: Date = new Date()
): string | null => {
  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime()) || Number.isNaN(now.getTime())) {
    return null;
  }

  const diffMs = Math.max(0, now.getTime() - date.getTime());

  if (diffMs < HOUR_MS) {
    return `${Math.max(1, Math.floor(diffMs / MINUTE_MS))}m ago`;
  }

  if (diffMs < DAY_MS) {
    return `${Math.floor(diffMs / HOUR_MS)}h ago`;
  }

  if (diffMs < WEEK_MS) {
    return `${Math.floor(diffMs / DAY_MS)}d ago`;
  }

  if (diffMs < MONTH_MS) {
    return `${Math.floor(diffMs / WEEK_MS)}w ago`;
  }

  if (diffMs < YEAR_MS) {
    return `${Math.min(11, Math.floor(diffMs / MONTH_MS))}mo ago`;
  }

  return `${Math.floor(diffMs / YEAR_MS)}y ago`;
};
